import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  ChefHat,
  Carrot,
  Calendar,
  CalendarDays,
  Sparkles,
  FolderPlus,
  Factory,
  FileSpreadsheet,
  Settings,
  Menu,
  X,
  LogOut,
  Printer,
  Layers,
  AlertTriangle,
  MapPin,
  RefreshCcw
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

const LOGO_URL = "/vprep-logo.png";

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/recipes', icon: ChefHat, label: 'Recipes' },
  { path: '/ingredients', icon: Carrot, label: 'Ingredients' },
  { path: '/rotation/regular', icon: Calendar, label: 'Regular Rotation' },
  { path: '/rotation/special', icon: Sparkles, label: 'Special Rotation' },
  { path: '/rotation/custom', icon: Layers, label: 'Custom Rotations' },
  { path: '/calendar', icon: CalendarDays, label: 'Live Calendar' },
  { path: '/extra-recipes', icon: FolderPlus, label: 'Extra Recipes' },
  { path: '/production', icon: Factory, label: 'Production' },
  { path: '/import-export', icon: FileSpreadsheet, label: 'Import/Export' },
];

const adminNavItems = [
  { path: '/settings', icon: Settings, label: 'Settings' },
];

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { user, logout, isAdmin, geofenceBlocked, geofenceMessage, checkingGeofence, recheckGeofence } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    setShowLogoutDialog(false);
    logout();
    navigate('/login');
  };

  // Show geofence blocked screen for managers
  if (geofenceBlocked && !isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#124242] to-[#0d3232] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <MapPin size={40} className="text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-stone-800 mb-2">Access Restricted</h1>
          <p className="text-stone-600 mb-6">{geofenceMessage}</p>
          
          <div className="space-y-3">
            <button
              onClick={recheckGeofence}
              disabled={checkingGeofence}
              className="w-full btn-primary justify-center"
            >
              {checkingGeofence ? (
                <span className="spinner" />
              ) : (
                <RefreshCcw size={18} />
              )}
              <span>{checkingGeofence ? 'Checking...' : 'Check Location Again'}</span>
            </button>
            
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="w-full btn-outline justify-center"
            >
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>
          
          <p className="text-xs text-stone-400 mt-6">
            Please move to the designated work area to access the app.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-stone-200 z-50 flex items-center justify-between px-4 no-print">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-stone-100 transition-colors"
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <img src={LOGO_URL} alt="VPrep" className="h-10" />
        <div className="w-10" /> {/* Spacer for balance */}
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 bg-white border-r border-stone-200 z-40 transition-transform duration-300 no-print
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-stone-200">
            <img src={LOGO_URL} alt="VPrep" className="h-16 mx-auto" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `nav-item ${isActive ? 'active' : ''}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon size={20} />
                  <span>{item.label}</span>
                </NavLink>
              ))}

              {isAdmin && (
                <>
                  <div className="border-t border-stone-200 my-4" />
                  <p className="px-3 py-2 text-xs font-semibold text-stone-400 uppercase tracking-wider">
                    Admin
                  </p>
                  {adminNavItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `nav-item ${isActive ? 'active' : ''}`
                      }
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon size={20} />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </>
              )}
            </div>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-stone-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#124242] flex items-center justify-center text-white font-semibold">
                {user?.username?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-stone-800 truncate">{user?.username}</p>
                <p className="text-xs text-stone-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={() => setShowLogoutDialog(true)}
              className="w-full btn-outline flex items-center justify-center gap-2 py-2"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <AlertTriangle size={22} className="text-amber-500" />
              Sign Out?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to sign out of your account?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 mt-4">
            <button
              onClick={() => setShowLogoutDialog(false)}
              className="btn-outline flex-1"
            >
              Cancel
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              data-testid="confirm-logout-btn"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-72 min-h-screen pt-16 lg:pt-0">
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
